import HowItWorks from './HowItWorks'
import Tooltip from '@mortgage-pos/ui/components/Tooltip'
import { Routes } from '@mortgage-pos/types'
import { useNavigate } from 'react-router-dom'
import CardsValueProps from './CardsValueProps'
import React, { FC, useEffect, useState } from 'react'
import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'
import newRelic from '@mortgage-pos/ui/services/newRelic'
import InfoBlock from '@mortgage-pos/ui/components/InfoBlock'
import { useStoreActions, useStoreState } from '@mortgage-pos/ui/store'
import { useVariation } from '@mortgage-pos/ui/services/featureFlags'
import { whatWeAssume } from '@mortgage-pos/ui/data/bankrateTooltipData'
import { supportedFileFormats } from '@mortgage-pos/ui/data/supportedFileFormats'
import FileUpload from '@mortgage-pos/ui/components/bankrateCompare/FileUpload'
import LoadingWithValueProps from '@mortgage-pos/ui/components/LoadingWithValueProps'
import incense from '@mortgage-pos/ui/incense'

interface UploaderProps {
  topSubHeader?: string
  title?: string
  subTitle?: string
  uploadButtonText?: string
  uploadErrorText?: string
  documentType?: string
}

const Uploader: FC<UploaderProps> = ({
  topSubHeader = '',
  title = '',
  subTitle = '',
  uploadButtonText = '',
  uploadErrorText = '',
  documentType = 'loanEstimate',
}) => {
  const navigate = useNavigate()

  const [uploadAttemptCount, setUploadAttemptCount] = useState<number>(0)
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false)
  const [showFormatErrorMessage, setShowFormatErrorMessage] =
    useState<boolean>(false)

  const { isLeComparisonRunning } = useStoreState((state) => state.leComparison)
  const { extractLEDetails, setIsLeComparisonRunning } = useStoreActions(
    (state) => state.leComparison
  )
  const { next } = useStoreActions((store) => store.questionnaire)
  const { elementClicked } = useStoreActions((store) => store.tracking)

  const showCompareDemo = useVariation(
    FEATURE_FLAG_TYPES.ENABLE_COMPARE_LE_DEMO
  )

  useEffect(() => {
    if (typeof showCompareDemo === 'boolean' && !showCompareDemo) {
      navigate(`/${Routes.APP_1003}`)
    }
  }, [navigate, showCompareDemo])

  useEffect(() => {
    newRelic.increment('compare_page_viewed')
  }, [])

  const handleFileUpload = async (file: File) => {
    setIsLeComparisonRunning(true)
    setShowErrorMessage(false)
    setShowFormatErrorMessage(false)

    elementClicked({
      elementType: 'Button',
      location: 'Compare Upload page',
      actionOutcome: 'Uploads a document for extraction',
      text: uploadButtonText,
    })

    const fileFormat = file.type ? file.type.split('/')[1] : ''

    if (!supportedFileFormats.includes(fileFormat.toLowerCase())) {
      setShowFormatErrorMessage(true)
      setIsLeComparisonRunning(false)

      // TODO short lived for qa and production testing
      incense()
        .details({
          name: 'Invalid File Type Uploaded',
          message: 'Attemted to upload invalid file type',
        })
        .safe({
          fileFormat,
          supportedFileFormats,
        })
        .error()
      return
    }

    let results
    try {
      setUploadAttemptCount(uploadAttemptCount + 1)
      results = await extractLEDetails({ file, documentType })
    } catch (e) {
      if (uploadAttemptCount > 2) {
        // Allow to proceed - compare flow uses visibleIfs to display extra page for manual LE data entry
        newRelic.increment('upload_loan_estimate.fail_forward')
        await next()
      }

      setShowErrorMessage(true)
      setIsLeComparisonRunning(false)
      return
    }

    // For POC purposes only
    if (new URLSearchParams(window.location.search).get('br-redirect')) {
      const bankrateBaseUrl =
        'https://www.bankrate.com/mortgages/mortgage-rates/?partnerId=br3&pid=br3&pointsChanged=false&searchChanged=false&disablePre=1&'
      const bankrateUrlParams = new URLSearchParams()
      const { leData, answersFromLe } = results
      bankrateUrlParams.append('refinanceCashOutAmount', leData.cashOut)
      bankrateUrlParams.append('refinanceLoanAmount', leData.loanAmount)
      bankrateUrlParams.append('refinanceLoanTerms', leData.term + 'yr')
      bankrateUrlParams.append('refinancePropertyType', 'SingleFamily')
      bankrateUrlParams.append('PrimaryResidence', 'PrimaryResidence')
      bankrateUrlParams.append('refinancePropertyValue', leData.propertyValue)
      bankrateUrlParams.append('zipCode', answersFromLe.zipCode)

      window.location.href = bankrateBaseUrl + bankrateUrlParams.toString()
    } else {
      await next()
    }
  }

  return (
    <>
      <div className="br-compare-page__heading-group">
        <p className="compare-page__subtitle">{topSubHeader}</p>
        <h1 className="br-compare-page__title">{title}</h1>

        {isLeComparisonRunning ? null : (
          <p className="compare-page__description">
            {subTitle}
            <Tooltip
              className="compare-page__legal-tooltip"
              title={whatWeAssume.title}
              text={whatWeAssume.text}
            />
          </p>
        )}
      </div>
      <div className="br-compare-page__upload-wrapper">
        {showErrorMessage ? (
          <InfoBlock type="danger">
            {uploadErrorText ||
              'Oops! We failed to to read your file. Please try again.'}
          </InfoBlock>
        ) : null}
        {showFormatErrorMessage ? (
          <InfoBlock type="danger">
            Please try uploading one of the supported formats: PDF, TIFF, JPG,
            JPEG, PNG, HEIC, HEIF.
          </InfoBlock>
        ) : null}
        {isLeComparisonRunning ? (
          <LoadingWithValueProps />
        ) : (
          <FileUpload
            fileUploadHandler={handleFileUpload}
            uploadButtonText={uploadButtonText}
            manualAddressClickHandler={next}
          />
        )}
      </div>
      <CardsValueProps />
      <HowItWorks />
    </>
  )
}

export default Uploader
